import React, { useState, useEffect } from 'react';
import { supabaseAdmin as supabase } from '../../lib/supabase.js';
import './TimetableOverrideManager.css';

const TimetableOverrideManager = ({ tenantId, schoolName }) => {
  const [activeTab, setActiveTab] = useState('classes');
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [overrideConfig, setOverrideConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadData();
  }, [tenantId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load classes, teachers, and students for this tenant
      const [classesResult, teachersResult, studentsResult] = await Promise.all([
        supabase.from('saas_classes').select('*').eq('tenant_id', tenantId),
        supabase.from('saas_teachers').select('*').eq('tenant_id', tenantId),
        supabase.from('saas_students').select('*').eq('tenant_id', tenantId)
      ]);

      setClasses(classesResult.data || []);
      setTeachers(teachersResult.data || []);
      setStudents(studentsResult.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage('Error loading data: ' + error.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const loadOverrideConfig = async (entityType, entityId) => {
    try {
      const { data, error } = await supabase
        .from('timetable_overrides')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setOverrideConfig(data?.config || null);
    } catch (error) {
      console.error('Error loading override config:', error);
      setOverrideConfig(null);
    }
  };

  const saveOverrideConfig = async (entityType, entityId, entityName) => {
    try {
      const { error } = await supabase
        .from('timetable_overrides')
        .upsert({
          tenant_id: tenantId,
          entity_type: entityType,
          entity_id: entityId,
          entity_name: entityName,
          config: overrideConfig,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setMessage(`Override configuration saved for ${entityName}!`);
      setMessageType('success');
    } catch (error) {
      console.error('Error saving override config:', error);
      setMessage('Error saving configuration: ' + error.message);
      setMessageType('error');
    }
  };

  const handleEntitySelect = (entity, entityType) => {
    setSelectedEntity({ ...entity, type: entityType });
    loadOverrideConfig(entityType, entity.id);
  };

  const updateOverrideConfig = (field, value) => {
    setOverrideConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateTimeSlotOverride = (periodSequence, field, value) => {
    setOverrideConfig(prev => ({
      ...prev,
      time_slot_overrides: {
        ...prev.time_slot_overrides,
        [periodSequence]: {
          ...prev.time_slot_overrides?.[periodSequence],
          [field]: value
        }
      }
    }));
  };

  const renderEntityList = () => {
    const entities = activeTab === 'classes' ? classes : 
                   activeTab === 'teachers' ? teachers : students;
    
    return (
      <div className="entity-list">
        <h3>
          {activeTab === 'classes' ? 'Classes' : 
           activeTab === 'teachers' ? 'Teachers' : 'Students'}
        </h3>
        <div className="entity-grid">
          {entities.map(entity => (
            <div 
              key={entity.id}
              className={`entity-card ${selectedEntity?.id === entity.id ? 'selected' : ''}`}
              onClick={() => handleEntitySelect(entity, activeTab.slice(0, -1))}
            >
              <div className="entity-icon">
                {activeTab === 'classes' ? '🏫' : 
                 activeTab === 'teachers' ? '👨‍🏫' : '👨‍🎓'}
              </div>
              <div className="entity-info">
                <h4>{entity.name || entity.teacher_name || entity.student_name}</h4>
                <p>{activeTab === 'classes' ? entity.class_name : 
                    activeTab === 'teachers' ? entity.subjects : 
                    entity.class_id}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderOverrideConfig = () => {
    if (!selectedEntity) {
      return (
        <div className="no-selection">
          <h3>Select an entity to configure overrides</h3>
          <p>Choose a class, teacher, or student from the list to set up timetable overrides.</p>
        </div>
      );
    }

    return (
      <div className="override-config">
        <div className="config-header">
          <h3>
            Override Configuration for {selectedEntity.name || selectedEntity.teacher_name || selectedEntity.student_name}
          </h3>
          <p className="entity-type">
            {selectedEntity.type === 'class' ? 'Class' : 
             selectedEntity.type === 'teacher' ? 'Teacher' : 'Student'} Override
          </p>
        </div>

        <div className="config-sections">
          {/* General Override Settings */}
          <div className="config-section">
            <h4>General Settings</h4>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={overrideConfig?.enabled || false}
                  onChange={(e) => updateOverrideConfig('enabled', e.target.checked)}
                />
                Enable timetable overrides for this {selectedEntity.type}
              </label>
            </div>
            <div className="form-group">
              <label>Override Reason:</label>
              <input
                type="text"
                value={overrideConfig?.reason || ''}
                onChange={(e) => updateOverrideConfig('reason', e.target.value)}
                placeholder="e.g., Special needs, Advanced class, etc."
                className="form-input"
              />
            </div>
          </div>

          {/* Time Slot Overrides */}
          <div className="config-section">
            <h4>Time Slot Overrides</h4>
            <p>Override specific time slots for this {selectedEntity.type}</p>
            <div className="time-slot-overrides">
              {Array.from({ length: 15 }, (_, i) => i + 1).map(periodSequence => (
                <div key={periodSequence} className="time-slot-override">
                  <div className="period-info">
                    <span className="period-number">Period {periodSequence}</span>
                    <span className="period-time">8:00-8:10</span>
                  </div>
                  <div className="override-controls">
                    <input
                      type="text"
                      placeholder="Override time range"
                      value={overrideConfig?.time_slot_overrides?.[periodSequence]?.time_range || ''}
                      onChange={(e) => updateTimeSlotOverride(periodSequence, 'time_range', e.target.value)}
                      className="form-input small"
                    />
                    <input
                      type="text"
                      placeholder="Override period node"
                      value={overrideConfig?.time_slot_overrides?.[periodSequence]?.period_node || ''}
                      onChange={(e) => updateTimeSlotOverride(periodSequence, 'period_node', e.target.value)}
                      className="form-input small"
                    />
                    <input
                      type="number"
                      placeholder="Override period number"
                      value={overrideConfig?.time_slot_overrides?.[periodSequence]?.period_number || ''}
                      onChange={(e) => updateTimeSlotOverride(periodSequence, 'period_number', parseInt(e.target.value))}
                      className="form-input small"
                      min="0"
                      max="10"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Day-Specific Overrides */}
          <div className="config-section">
            <h4>Day-Specific Overrides</h4>
            <div className="day-overrides">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                <div key={day} className="day-override">
                  <h5>{day}</h5>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={overrideConfig?.day_overrides?.[day]?.enabled || false}
                        onChange={(e) => updateOverrideConfig(`day_overrides.${day}.enabled`, e.target.checked)}
                      />
                      Override {day} schedule
                    </label>
                  </div>
                  {overrideConfig?.day_overrides?.[day]?.enabled && (
                    <div className="day-override-details">
                      <input
                        type="text"
                        placeholder="Special note for this day"
                        value={overrideConfig?.day_overrides?.[day]?.note || ''}
                        onChange={(e) => updateOverrideConfig(`day_overrides.${day}.note`, e.target.value)}
                        className="form-input"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              onClick={() => saveOverrideConfig(
                selectedEntity.type, 
                selectedEntity.id, 
                selectedEntity.name || selectedEntity.teacher_name || selectedEntity.student_name
              )}
              className="btn btn-primary"
            >
              Save Override Configuration
            </button>
            <button
              onClick={() => setOverrideConfig(null)}
              className="btn btn-secondary"
            >
              Clear Overrides
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="timetable-override-manager">
        <div className="loading">Loading override management...</div>
      </div>
    );
  }

  return (
    <div className="timetable-override-manager">
      <div className="manager-header">
        <h2>🎛️ Timetable Override Manager</h2>
        <p>Configure individual overrides for classes, teachers, and students</p>
        <p className="school-info">School: {schoolName} | Tenant: {tenantId}</p>
      </div>

      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}

      <div className="manager-content">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'classes' ? 'active' : ''}`}
            onClick={() => setActiveTab('classes')}
          >
            🏫 Classes ({classes.length})
          </button>
          <button
            className={`tab ${activeTab === 'teachers' ? 'active' : ''}`}
            onClick={() => setActiveTab('teachers')}
          >
            👨‍🏫 Teachers ({teachers.length})
          </button>
          <button
            className={`tab ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            👨‍🎓 Students ({students.length})
          </button>
        </div>

        <div className="manager-body">
          <div className="left-panel">
            {renderEntityList()}
          </div>
          <div className="right-panel">
            {renderOverrideConfig()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimetableOverrideManager;
